import { CalendarDate } from "@sp-crm/core";
import { useDebouncedState } from "components/shared/hooks";
import { CalendarDateInput } from "components/ui/calendar-date";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";

export interface DatePreset {
    label: string;
    getRange: () => { start: CalendarDate; end: CalendarDate };
}

export const getDatePresets = (): DatePreset[] => {
    const today = CalendarDate.today;
    return [
        {
            label: "Year to Date",
            getRange: () => ({
                start: new CalendarDate(today.year, 1, 1),
                end: today,
            }),
        },
        {
            label: "This Month",
            getRange: () => ({
                start: new CalendarDate(today.year, today.month, 1),
                end: today,
            }),
        },
        {
            label: "Last Month",
            getRange: () => {
                const lastMonth =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 12, 1)
                        : new CalendarDate(today.year, today.month - 1, 1);
                const endDay = new Date(lastMonth.year, lastMonth.month, 0).getDate();
                return {
                    start: lastMonth,
                    end: new CalendarDate(lastMonth.year, lastMonth.month, endDay),
                };
            },
        },
        {
            label: "This Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                return {
                    start: new CalendarDate(today.year, q * 3 + 1, 1),
                    end: today,
                };
            },
        },
        {
            label: "Last Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                const prevQ = q === 0 ? 3 : q - 1;
                const year = q === 0 ? today.year - 1 : today.year;
                const startMonth = prevQ * 3 + 1;
                const endMonth = startMonth + 2;
                const endDay = new Date(year, endMonth, 0).getDate();
                return {
                    start: new CalendarDate(year, startMonth, 1),
                    end: new CalendarDate(year, endMonth, endDay),
                };
            },
        },
        {
            label: "Last 12 Months",
            getRange: () => {
                const start =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 1, today.day)
                        : new CalendarDate(today.year - 1, today.month, today.day);
                return { start, end: today };
            },
        },
        {
            label: "Last Year",
            getRange: () => ({
                start: new CalendarDate(today.year - 1, 1, 1),
                end: new CalendarDate(today.year - 1, 12, 31),
            }),
        },
    ];
};

const CUSTOM_VALUE = "__custom__";

const DatePresetButtons: React.FC<{
    presets: DatePreset[];
    activePreset: string | null;
    onSelect: (label: string, start: CalendarDate, end: CalendarDate) => void;
}> = ({ presets, activePreset, onSelect }) => {
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const label = e.target.value;
        if (label === CUSTOM_VALUE) return;
        const preset = presets.find(p => p.label === label);
        if (preset) {
            const range = preset.getRange();
            onSelect(preset.label, range.start, range.end);
        }
    };

    return (
        <>
            <div className="hidden xl:flex flex-wrap gap-1">
                {presets.map(p => (
                    <SecondaryCompactButton
                        key={p.label}
                        selected={activePreset === p.label}
                        onClick={() => {
                            const range = p.getRange();
                            onSelect(p.label, range.start, range.end);
                        }}>
                        {p.label}
                    </SecondaryCompactButton>
                ))}
            </div>
            <div className="w-44 shrink-0 xl:hidden">
                <Select
                    label="Date Range"
                    textSize="sm"
                    value={activePreset ?? CUSTOM_VALUE}
                    onChange={handleSelectChange}>
                    {activePreset === null ? (
                        <option value={CUSTOM_VALUE}>Custom</option>
                    ) : null}
                    {presets.map(p => (
                        <option key={p.label} value={p.label}>
                            {p.label}
                        </option>
                    ))}
                </Select>
            </div>
        </>
    );
};

export interface DateRangeValue {
    startDate: CalendarDate;
    endDate: CalendarDate;
    activePreset: string | null;
}

const typedRangeDelayMs = 600;

const rangeKey = (range: DateRangeValue): string =>
    `${range.startDate.toString()}|${range.endDate.toString()}|${range.activePreset ?? ""}`;

const hasCompleteYear = (date: CalendarDate): boolean => date.year >= 1000;

export const DateRangeControl: React.FC<{
    value: DateRangeValue;
    onChange: (value: DateRangeValue) => void;
    presets?: DatePreset[];
}> = ({ value, onChange, presets }) => {
    const options = React.useMemo(() => presets ?? getDatePresets(), [presets]);
    const [draft, setDraft, settledDraft] = useDebouncedState(value, typedRangeDelayMs);
    const valueKey = rangeKey(value);
    const latest = React.useRef({ value, onChange });
    const lastEmittedKey = React.useRef(valueKey);

    React.useEffect(() => {
        latest.current = { value, onChange };
    });

    React.useEffect(() => {
        lastEmittedKey.current = valueKey;
        setDraft(current =>
            rangeKey(current) === valueKey ? current : latest.current.value,
        );
    }, [valueKey, setDraft]);

    React.useEffect(() => {
        const settledKey = rangeKey(settledDraft);
        if (settledKey === lastEmittedKey.current) {
            return;
        }
        lastEmittedKey.current = settledKey;
        latest.current.onChange(settledDraft);
    }, [settledDraft]);

    const applyPreset = (label: string, start: CalendarDate, end: CalendarDate): void => {
        const next: DateRangeValue = {
            startDate: start,
            endDate: end,
            activePreset: label,
        };
        lastEmittedKey.current = rangeKey(next);
        setDraft(next);
        onChange(next);
    };

    const typeStartDate = (date: CalendarDate | null): void => {
        if (date && hasCompleteYear(date)) {
            setDraft(current => ({ ...current, startDate: date, activePreset: null }));
        }
    };

    const typeEndDate = (date: CalendarDate | null): void => {
        if (date && hasCompleteYear(date)) {
            setDraft(current => ({ ...current, endDate: date, activePreset: null }));
        }
    };

    return (
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
            <div className="w-40 shrink-0">
                <CalendarDateInput
                    label="Start"
                    textSize="sm"
                    value={draft.startDate}
                    onChange={typeStartDate}
                />
            </div>
            <div className="w-40 shrink-0">
                <CalendarDateInput
                    label="End"
                    textSize="sm"
                    value={draft.endDate}
                    onChange={typeEndDate}
                />
            </div>
            <DatePresetButtons
                presets={options}
                activePreset={draft.activePreset}
                onSelect={applyPreset}
            />
        </div>
    );
};
